import { Divider, Grid, Stack, Tab, Tabs } from "@mui/material";
import React, { useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer.tsx";
import type { DraggableMetaType, FieldWrapper, ReportField } from "../../../../../../../../api/types/svTypes.ts";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch";
import { selectCurrentReportType } from "../../../../store/selectors/currentReportSelectors";
import { selectArguments, selectSorts, selectValues } from "../../../../store/selectors/fieldsStateSelectors";
import { selectEditDisabled, selectSelectedBuilderTab } from "../../../../store/selectors/uiSettingsSelectors";
import type { BuilderTab } from "../../../../store/types/uiSettingsState.types";
import { createUiSettingsAction } from "../../../../store/utils/storeUtils";
import type { TableComponentProps } from "../../../viewer/common/Types.ts";
import useCanAddCustomFields from "../../common/hooks/useCanAddCustomFields.ts";
import ConditionsDropFieldsContainer from "../../pivot/table/conditions/ConditionsDropFieldsContainer.tsx";
import ValuesDropFieldsContainer from "../../pivot/table/values/ValuesDropFieldsContainer.tsx";
import SortDropsFieldsContainer from "../../tabular/table/SortDropsFieldsContainer.tsx";
import StyleContainer from "../style/StyleContainer.tsx";
import ArgumentDropFieldsContainer from "./arguments/ArgumentDropFieldsContainer.tsx";
import { canDropValueItem, SINGLE_VALUE_REPORT_TYPE } from "./canDropValue.ts";
import FormatContainer from "./FormatContainer.tsx";

export default function TableComponent({ ReportTypeSelector }: TableComponentProps) {
  const reportType = useSelector(selectCurrentReportType);
  const argumentsAreaValues = useSelector(selectArguments);
  const sortingAreaValues = useSelector(selectSorts);
  const valuesAreaValues = useSelector(selectValues);
  const dispatch = useAppDispatch();
  const selectedTab = useSelector(selectSelectedBuilderTab);
  const editDisabled = useSelector(selectEditDisabled);
  const valuesRef = useRef(valuesAreaValues);
  useEffect(() => {
    valuesRef.current = valuesAreaValues;
  }, [valuesAreaValues]);

  const reportTypeRef = useRef(reportType);
  useEffect(() => {
    reportTypeRef.current = reportType;
  }, [reportType]);

  const availableSortFields = React.useMemo(() => {
    const result = argumentsAreaValues.filter((f) => !sortingAreaValues.find((v) => v.meta.name === f.meta.name));
    return result;
  }, [argumentsAreaValues, sortingAreaValues]);

  const handleCanDropItem = useCallback(
    (item: FieldWrapper<ReportField> | DraggableMetaType) =>
      canDropValueItem(item, reportTypeRef.current, valuesRef.current),
    []
  );
  const canAddCustomField = useCanAddCustomFields();

  //As far as a custom field has to reference any measure, it is not allowed for single value reports
  const isSingleValueReportType = reportType === SINGLE_VALUE_REPORT_TYPE;

  return (
    <Stack spacing="1rem" useFlexGap sx={{ flex: 1, mt: "1rem", width: "100%" }}>
      <Grid container sx={{ px: "1.3rem", width: "100%" }}>
        {ReportTypeSelector}
        <Tabs
          value={selectedTab}
          sx={{ borderBottom: "none" }}
          onChange={(_, value: BuilderTab) => dispatch(createUiSettingsAction("setTab", value))}
        >
          <Tab value="Fields" label="Fields" />
          <Tab value="Settings" label="Settings" />
          <Tab value="Format" label="Format" />
        </Tabs>
      </Grid>
      <Divider sx={{ mt: -2 }} />
      {selectedTab === "Fields" && (
        <ScrollableFlexContainer
          scrollContainerSx={{ display: "flex", flexDirection: "column", gap: "1.5rem", px: "1.3rem" }}
        >
          <ConditionsDropFieldsContainer disableEqualitySelection={true} readonlyMode={editDisabled} />
          <ArgumentDropFieldsContainer readonlyMode={editDisabled} />
          <ValuesDropFieldsContainer
            fields={argumentsAreaValues}
            maxFields={isSingleValueReportType ? 1 : undefined}
            canDropItem={handleCanDropItem}
            canAddCustomField={!isSingleValueReportType && canAddCustomField}
            readonlyMode={editDisabled}
          />
          <SortDropsFieldsContainer availableFields={availableSortFields} readonlyMode={editDisabled} />
        </ScrollableFlexContainer>
      )}
      {selectedTab === "Settings" && <StyleContainer readonlyMode={editDisabled} />}
      {selectedTab === "Format" && <FormatContainer readonlyMode={editDisabled} />}
    </Stack>
  );
}
